import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Button,
  Stack,
  Dialog,
  Checkbox,
  TextField,
  InputAdornment,
  Avatar,
  Skeleton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  Close as CloseIcon,
  Speed as SpeedIcon,
  Navigation as NavigationIcon,
  Schedule as ClockIcon,
  DirectionsCarOutlined as DirectionsCarOutlinedIcon,
  Memory as MemoryIcon,
  BatteryFull as BatteryIcon,
  SignalCellularAlt as SignalIcon,
  Satellite as SatelliteIcon,
  PowerOff as PowerOffIcon,
  MyLocation as MyLocationIcon,
  KeyboardDoubleArrowRight as CollapseIcon,
  Apartment as LandmarkIcon,
  AccountTree as HistoryMapIcon,
  Route as RouteIcon,
  Send as ShareIcon,
  Add as PlusIcon,
  Remove as MinusIcon,
  InfoOutlined as InfoIcon,
  Engineering as EngineIcon,
  LayersOutlined as MapTypeIcon,
  Traffic as TrafficIcon,
  CenterFocusStrong as FitBoundsIcon,
  ContentCopy as ContentCopyIcon,
  MoreHoriz as MoreHorizIcon,
  Refresh as RefreshIcon,
  NotificationsNone as NotificationsIcon,
  ShieldOutlined as GeofenceIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  DirectionsCarFilledOutlined as AssignIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';

import { useSocket } from '../../socket/SocketContext';
import { useGetUsersByRoleQuery } from '../../redux/admin/userApi';
import { useUpdateDeviceMutation } from '../../redux/admin/deviceApi';
import { useGetAllLiteDevicesQuery } from '../../redux/admin/adminApi';
import { getCanonicalVehicleStatus, getStatusConfig, getResolvedLiveSnapshot, resolveStatus } from '../../utils/vehicleStatus';
import { normalizeAcc } from '../../utils/normalizeIgnition';
import { fetchDeviceLiveData } from '../../utils/apiHelpers';
import { resolveTrackingState } from '../../utils/trackingStateEngine';
import { useStatus } from '../../popup/NotificationContext';
import api from '../../redux/apis/api';
import { BrandSelectionDialog } from '../modals/wizard/BrandDialogs';

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDisplayTime = (value) => {
  const ts = toTimestampMs(value);
  if (!ts) return "NA";
  return new Date(ts).toLocaleString();
};

const pickFirstTimeValue = (...values) => {
  for (const value of values) {
    if (toTimestampMs(value)) return value;
  }
  return null;
};

const formatDriftText = (driftMs = 0) => {
  const sign = driftMs >= 0 ? "+" : "-";
  const absMs = Math.abs(Number(driftMs) || 0);
  const totalMinutes = Math.round(absMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return `${sign}${parts.join(" ")}`;
};


const formatStatusLabel = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "inactive") return "NOT REPORTING";
  if (key === "no_data" || key === "nodata") return "NO DATA";
  return key.toUpperCase();
};

const formatNotReportingSince = (timestamp) => {
  const ts = toTimestampMs(timestamp);
  if (!ts) return "";
  const diff = Math.max(0, Date.now() - ts);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `since ${h}h ${m}m`;
};

const EMPTY_DAILY_STATS = {
  distance: 0,
  runningTime: 0,
  idleTime: 0,
  stopTime: 0,
  inactiveTime: 0,
  maxSpeed: 0,
  avgSpeed: 0,
  fuelConsumption: 0,
};
const MOVING_STATUS_SPEED_KMH = Number(process.env.REACT_APP_MOVING_SPEED_KMH || 5);
const STATIONARY_FLICKER_GUARD_MS = 2 * 60 * 1000;
const TIME_DEBUG_UI_ENABLED = String(process.env.REACT_APP_TIME_DEBUG_LOGS || "true").toLowerCase() !== "false";



const formatAddress = (address) => {
  if (!address) return null;
  if (typeof address === 'string') return address;
  if (address.full) return address.full;
  
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.country) parts.push(address.country);
  
  return parts.length > 0 ? parts.join(', ') : null;
};

const formatCompassDirection = (heading) => {
  const value = Number(heading);
  if (!Number.isFinite(value)) return "NA";
  const normalized = ((value % 360) + 360) % 360;
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return directions[Math.round(normalized / 45) % 8];
};

const normalizeAddressText = (value) => {
  const text = formatAddress(value);
  if (!text) return null;
  const normalized = text.trim();
  if (
    /^location at\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/i.test(normalized) ||
    /^(unknown location|unknown address|location unavailable|address unavailable|resolving address\.{0,3}|loading address\.{0,3}|n\/a|na|null)$/i.test(normalized)
  ) {
    return null;
  }
  return normalized;
};

const safeLower = (value) => String(value ?? "").toLowerCase();

const prettyNotificationEvent = (value) => {
  if (!value) return "Unknown Event";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getNotificationCoords = (alarm) => {
  const lat = Number(
    alarm?.location?.latitude ??
    alarm?.location?.lat ??
    alarm?.latitude ??
    alarm?.lat
  );
  const lng = Number(
    alarm?.location?.longitude ??
    alarm?.location?.lng ??
    alarm?.longitude ??
    alarm?.lng
  );
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const formatNotificationTime = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const RightSidebarPanel = ({
  device, 
  onDeviceUpdated,
  isOpen,
  onClose, 
  getDirections, 
  handleCommand,
  onStartTrip, 
  onEndTrip, 
  onGetMyTrip,
  activeTrip, 
  onOpenCamera,
  onShowHistory, 
  onCreateGeofence,
  onGetMyRoute,
  onOpenReport,
  onSaveBookmark,
  topOffset = 0,
  showTraffic = false,
  setShowTraffic,
  mapType = 'roadmap',
  setMapType,
  followMode = false,
  setFollowMode,
  routesOverlayActive = false,
  hasSelectedDevice = false,
  mobileMode = false,
  onLocateMe,
  onFitBounds,
  onZoomIn,
  onZoomOut,
  getVehicleIcon,
  notifications = [],
  onSelectNotification,
  selectedNotificationId,
  sidebarGeofences = [],
  onCreateSidebarGeofence,
  onEditSidebarGeofence,
  onDeleteSidebarGeofence,
  onAssignSidebarGeofence,
  onSelectGeofence,
  selectedGeofenceId,
  sidebarLandmarks = [],
  onSelectLandmark,
  selectedLandmarkId,
  onCreateSidebarLandmark,
  onUpdateSidebarLandmark,
  onDeleteSidebarLandmark,
  onAssignSidebarLandmark,
  landmarkSaving = false,
  currentContextDevice = null,
  onLandmarkDraftChange,
  landmarkDraftValue = null,
  onLandmarkFocusChange,
  onLandmarkPlacementModeChange,
}) => {
  const currentUser = useSelector((state) => state.auth?.user);
  const STALE_STATUS_DURATION_MS = 2 * 60 * 60 * 1000;
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptopViewport = useMediaQuery(theme.breakpoints.down("lg"));
  const isShortViewport = useMediaQuery("(max-height: 900px)");
  const isVerySmallViewport = useMediaQuery("(max-width: 520px)");
  const isCompactDesktop = !isViewportMobile && (isLaptopViewport || isShortViewport);
  const isMobile = mobileMode || isViewportMobile;
  const desktopPanelHeight = isCompactDesktop
    ? 'min(252px, calc(100dvh - var(--tracking-shell-panels-top, 122px) - 22px))'
    : 'min(292px, calc(100dvh - var(--tracking-shell-panels-top, 122px) - 24px))';
  // ✅ ALL HOOKS AT TOP
  const [dailyStats, setDailyStats] = useState(EMPTY_DAILY_STATS);

  const panelRef = useRef(null);
  const { connected, getDeviceSocketData, on, joinDevice, leaveDevice } = useSocket();
  const { showStatus } = useStatus();
  const [updateDevice, { isLoading: isAssigningDriver }] = useUpdateDeviceMutation();
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [driverSearch, setDriverSearch] = useState("");
  const [isUpdatingRouteState, setIsUpdatingRouteState] = useState(false);
  const [assignedDriver, setAssignedDriver] = useState({
    name: "",
    mobile: "",
    id: "",
  });

  const [liveData, setLiveData] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [isLive, setIsLive] = useState(false);
  
  const [resolvedAddress, setResolvedAddress] = useState(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [landmarkFormOpen, setLandmarkFormOpen] = useState(false);
  const [landmarkMode, setLandmarkMode] = useState("create");
  const [landmarkEditingId, setLandmarkEditingId] = useState(null);
  const [landmarkForm, setLandmarkForm] = useState({
    name: "",
    category: "others",
    address: "",
    description: "",
    latitude: null,
    longitude: null,
    radius: 30,
  });
  const [landmarkLocationQuery, setLandmarkLocationQuery] = useState("");
  const [landmarkLocationLoading, setLandmarkLocationLoading] = useState(false);
  const [landmarkFormError, setLandmarkFormError] = useState("");
  const [landmarkPlacementActive, setLandmarkPlacementActive] = useState(false);

  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastUpdateRef = useRef(0);
  const updateIntervalRef = useRef(null);
  const geocodeMetaRef = useRef({ key: '', lat: null, lng: null, at: 0 });
  const trackingStateRef = useRef(null);
  const assignedDriverDeviceIdRef = useRef("");
  const { data: driversData = [] } = useGetUsersByRoleQuery("driver", {
    skip: !assignDriverOpen,
  });
  const { data: allDevicesForDriverAssign = [] } = useGetAllLiteDevicesQuery(undefined, {
    skip: !assignDriverOpen,
  });

  const isExplicitStationaryStatus = useCallback((status) => {
    const key = String(status || "").trim().toLowerCase();
return ["idle", "stopped", "inactive", "expired", "offline", "no_data", "nodata"].includes(key);
  }, []);



  // Socket Listener
  useEffect(() => {
    if (!connected || !device?._id) return;

    const handleLocationUpdate = (data) => {
      const deviceStatus = resolveStatus(device, {});
      if (deviceStatus === 'expired') return;

      const updateDeviceId = data.deviceId || data.device_id || data.id || data._id;
      const updateImei = String(data.imei || "");
      const matchesDevice =
        String(updateDeviceId || "") === String(device._id || "") ||
        (updateImei && String(device?.imei || "") === updateImei);
      if (!matchesDevice) return;

      const packetTimestamp =
        data.packetReceivedAt ??
        data.lastSocketUpdate ??
        data.packetTime ??
        data.serverReceivedAt ??
        data.receivedAt ??
        data.timestamp ??
        data.serverTime ??
        Date.now();

      const lat = data.latitude ?? data.lat;
      const lng = data.longitude ?? data.lng;
      const hasCoords = typeof lat === 'number' && typeof lng === 'number' && lat !== 0 && lng !== 0;
      const incomingDeviceTime = pickFirstTimeValue(
        data.deviceTime,
        data.correctedTime,
        data.timestamp,
        data.packetTime
      );
      const incomingServerTime = pickFirstTimeValue(
        data.serverTime,
        data.serverReceivedAt,
        data.receivedAt,
        data.packetReceivedAt,
        packetTimestamp
      );
      const incomingCorrectedTime = pickFirstTimeValue(
        data.correctedTime,
        data.timestamp,
        data.packetTime,
        incomingDeviceTime
      );

      setLiveData(prev => {
        const nextRawSpeed = data.speed ?? data.currentSpeed;
        const nextAccStatus =
          data.accStatus ?? data.acc ?? data.ignition ?? prev.accStatus ?? prev.acc ?? prev.ignition ?? null;
        const nextIgnition =
          data.ignition ?? data.accStatus ?? data.acc ?? prev.ignition ?? prev.accStatus ?? prev.acc ?? null;
        const incomingVehicleStatus =
          data.vehicleStatus ??
          data.vehicle_status ??
          null;
        const normalizedIncomingStatus = String(incomingVehicleStatus || "").trim().toLowerCase();
        const shouldForceZeroSpeed =
          (
            isExplicitStationaryStatus(normalizedIncomingStatus) ||
            nextAccStatus === false
          ) &&
          (
            nextRawSpeed === null ||
            nextRawSpeed === undefined ||
            Number(nextRawSpeed) <= 0
          );
        const shouldKeepPrevSpeed =
          !shouldForceZeroSpeed &&
          !hasCoords &&
          Number(nextRawSpeed ?? prev?.speed ?? 0) === 0;
        const prevTimestampMs = toTimestampMs(prev?.timestamp ?? prev?.serverTime ?? prev?.deviceTime) || 0;
        const prevWasMovingRecently =
          prevTimestampMs > 0 &&
          Date.now() - prevTimestampMs <= STATIONARY_FLICKER_GUARD_MS &&
          Number(prev?.speed || 0) >= MOVING_STATUS_SPEED_KMH &&
          String(prev?.vehicleStatus || "").trim().toLowerCase() === "running";
        const shouldIgnoreTransientStationary =
          prevWasMovingRecently &&
          !hasCoords &&
          (
            String(incomingVehicleStatus || "").trim().toLowerCase() === 'stopped' ||
            nextAccStatus === false
          ) &&
          (
            nextRawSpeed === null ||
            nextRawSpeed === undefined ||
            Number(nextRawSpeed) <= 0
          );
        const shouldKeepPrevStatus =
          !shouldIgnoreTransientStationary &&
          !hasCoords &&
          (
            incomingVehicleStatus === null ||
            incomingVehicleStatus === undefined ||
            incomingVehicleStatus === '' ||
            (
              String(incomingVehicleStatus).trim().toLowerCase() === 'stopped' &&
              Number(nextRawSpeed ?? prev?.speed ?? 0) <= 0
            )
          );
        const nextVehicleStatus = shouldIgnoreTransientStationary
          ? prev.vehicleStatus
          : getCanonicalVehicleStatus(
              {
                ...device,
                liveData: {
                  ...(device?.liveData || {}),
                  ...(prev || {}),
                },
              },
              {
                ...prev,
                ...data,
                accStatus: nextAccStatus,
                ignition: nextIgnition,
                speed: shouldForceZeroSpeed
                  ? 0
                  : shouldKeepPrevSpeed
                    ? prev.speed
                    : nextRawSpeed ?? prev.speed,
                vehicleStatus: shouldKeepPrevStatus ? prev.vehicleStatus : incomingVehicleStatus ?? prev.vehicleStatus,
                timestamp: packetTimestamp,
                packetTime: packetTimestamp,
                lastSocketUpdate: Date.now(),
              }
            );

        console.log("[SPEED_FLOW][UI]", String(device?.imei || device?._id || ""), {
          stage: "RightInfoPanel.socket",
          incomingSpeed: nextRawSpeed ?? null,
          appliedSpeed: shouldForceZeroSpeed ? 0 : shouldKeepPrevSpeed ? prev.speed : nextRawSpeed ?? prev.speed,
          incomingVehicleStatus,
          appliedVehicleStatus: nextVehicleStatus,
          hasCoords,
        });

        return {
          ...prev,
          latitude: hasCoords ? lat : prev.latitude,
          longitude: hasCoords ? lng : prev.longitude,
          speed: shouldIgnoreTransientStationary
            ? prev.speed
            : shouldForceZeroSpeed
            ? 0
            : shouldKeepPrevSpeed
            ? prev.speed
            : nextRawSpeed ?? prev.speed,
          heading: data.heading ?? data.course ?? data.bearing ?? prev.heading,
          accStatus: shouldIgnoreTransientStationary ? prev.accStatus : nextAccStatus,
          acc: shouldIgnoreTransientStationary ? prev.acc : nextAccStatus,
          ignition: shouldIgnoreTransientStationary ? prev.ignition : nextIgnition,
          vehicleStatus: nextVehicleStatus,
          movementState: data.movementState ?? data.movement_state ?? prev.movementState,
          fuelLevel: data.fuelLevel ?? data.fuel ?? prev.fuelLevel,
          battery: data.battery ?? data.batteryVoltage ?? prev.battery,
          satellites: data.satellites ?? data.sats ?? prev.satellites,
          rssi: data.rssi ?? data.signalStrength ?? prev.rssi,
          temperature: data.temperature ?? data.temp ?? prev.temperature,
          address: formatAddress(data.address) ?? prev.address,
          timestamp: packetTimestamp,
          deviceTime: incomingDeviceTime ?? prev.deviceTime ?? null,
          serverTime: incomingServerTime ?? prev.serverTime ?? packetTimestamp,
          correctedTime: incomingCorrectedTime ?? prev.correctedTime ?? packetTimestamp,
          isDrifted: Boolean(data.isDrifted ?? prev.isDrifted),
          driftMs: Number(data.driftMs ?? prev.driftMs ?? 0) || 0,
          driftSeconds: Number(data.driftSeconds ?? prev.driftSeconds ?? 0) || 0,
          packetTime: packetTimestamp,
          packetReceivedAt: Date.now(),
          lastSocketUpdate: Date.now(),
          locationInherited: data.locationInherited ?? (!hasCoords),
          hasLiveLocation: hasCoords,
        };
      });

      setLastUpdateTime(Date.now());
      setIsLive(true);
      setRefreshCounter(prev => prev + 1);
      lastUpdateRef.current = Date.now();
    };

    joinDevice?.(device._id);
    const offPacket = on?.('gps_packet', handleLocationUpdate);
    const offGps = on?.('gps_update', handleLocationUpdate);
    const offLocation = on?.('location_update', handleLocationUpdate);
    const offStatus = on?.('status_update', handleLocationUpdate);
    const offVehicle = on?.('vehicle_update', handleLocationUpdate);
    const offDevice = on?.('device_update', handleLocationUpdate);

    return () => {
      offPacket?.();
      offGps?.();
      offLocation?.();
      offStatus?.();
      offVehicle?.();
      offDevice?.();
      leaveDevice?.(device._id);
    };
  }, [connected, on, joinDevice, leaveDevice, device?._id, device?.imei]);

  // Auto-refresh
  useEffect(() => {
    if (!isOpen) return;
    updateIntervalRef.current = setInterval(() => {
      if (lastUpdateRef.current > 0) {
        setRefreshCounter(prev => prev + 1);
      }
    }, 2000);
    return () => clearInterval(updateIntervalRef.current);
  }, [isOpen]);

  // Reset on device change
  useEffect(() => {
    if (!device) {
      setLiveData({});
      setLastUpdateTime(null);
      setIsLive(false);
      setResolvedAddress(null);
      setRefreshCounter(0);
      lastUpdateRef.current = 0;
      trackingStateRef.current = null;
      return;
    }
    setLiveData({});
    setLastUpdateTime(null);
    setIsLive(false);
    setResolvedAddress(null);
      setCollapsed(false);
      setActiveTab('info');
      setLandmarkFormOpen(false);
      setRefreshCounter(0);
    lastUpdateRef.current = 0;
    trackingStateRef.current = null;
  }, [device?._id]);

  useEffect(() => {
    onLandmarkPlacementModeChange?.(Boolean(landmarkFormOpen && landmarkPlacementActive));
  }, [landmarkFormOpen, landmarkPlacementActive, onLandmarkPlacementModeChange]);

  useEffect(() => {
    if (!landmarkFormOpen) {
      onLandmarkFocusChange?.(null);
      return;
    }
    const latitude = Number(landmarkForm?.latitude);
    const longitude = Number(landmarkForm?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      onLandmarkFocusChange?.(null);
      return;
    }
    onLandmarkFocusChange?.({ latitude, longitude, lat: latitude, lng: longitude });
  }, [landmarkForm?.latitude, landmarkForm?.longitude, landmarkFormOpen, onLandmarkFocusChange]);

  useEffect(() => {
    if (!landmarkFormOpen || !landmarkDraftValue) return;
    const latitude = Number(landmarkDraftValue?.latitude ?? landmarkDraftValue?.lat);
    const longitude = Number(landmarkDraftValue?.longitude ?? landmarkDraftValue?.lng);
    const radius = Math.max(0, Number(landmarkDraftValue?.radius ?? landmarkDraftValue?.radiusMeters ?? 30));
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    setLandmarkPlacementActive(true);
    setLandmarkForm((prev) => ({
      ...prev,
      latitude,
      longitude,
      radius,
    }));
  }, [landmarkDraftValue, landmarkFormOpen]);

  useEffect(() => {
    if (!device?._id) {
      assignedDriverDeviceIdRef.current = "";
      setAssignedDriver({ name: "", mobile: "", id: "" });
      return;
    }

    const nextAssignedDriver = {
      id: String(device?.driverId || device?.driver?.driverId || ""),
      name:
        device?.driver?.driverName ||
        device?.driverName ||
        device?.driver?.name ||
        "",
      mobile:
        device?.driver?.contactNumber ||
        device?.driverContact ||
        device?.driverNumber ||
        device?.mobileNumber ||
        device?.driver?.mobileNo ||
        "",
    };

    const hasIncomingDetails = Boolean(nextAssignedDriver.name || nextAssignedDriver.mobile);
    const deviceChanged =
      assignedDriverDeviceIdRef.current !== String(device?._id || "");

    setAssignedDriver((prev) => {
      const hasLocalDriver = Boolean(prev.name || prev.mobile || prev.id);
      if (deviceChanged) return nextAssignedDriver;

      // If backend/device payload has usable driver details, treat it as source of truth.
      if (hasIncomingDetails) return nextAssignedDriver;

      // If only driver id is present but name/mobile are missing, don't clobber local UI state.
      if (nextAssignedDriver.id) {
        const sameId = String(prev.id || "") === String(nextAssignedDriver.id || "");
        if (sameId) return { ...prev, id: nextAssignedDriver.id };
        return nextAssignedDriver;
      }

      // No incoming driver; clear only if we didn't already have something.
      if (!hasLocalDriver) return nextAssignedDriver;
      return prev;
    });
    assignedDriverDeviceIdRef.current = String(device?._id || "");
  }, [device]);

  // Poll live data
  useEffect(() => {
    if (!isOpen || !device?._id) return;
    let cancelled = false;

    const pullLive = async () => {
      try {
        const latest = await fetchDeviceLiveData(device._id);
        if (cancelled || !latest?.liveData) return;
        setLiveData((prev) => ({
          ...prev,
          ...latest.liveData,
        }));
      } catch (err) {
        // keep socket + list fallback
      }
    };

    pullLive();
    const id = setInterval(pullLive, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isOpen, device?._id]);

  // Computed data
  const computedData = useMemo(() => {
    if (!device) {
      return {
        speed: 0, lat: null, lng: null, heading: 0, accStatus: false,
        vehicleStatus: 'no_data', source: 'none', dataAge: Infinity,
        isFresh: false, freshness: null
      };
    }

    const getPanelTimestamp = (value) =>
      toTimestampMs(
        value?.packetReceivedAt ??
          value?.lastSocketUpdate ??
          value?.packetTime ??
          value?.serverReceivedAt ??
          value?.receivedAt ??
          value?.timestamp ??
          value?.serverTime ??
          value?.lastDataReceivedAt ??
          value?.lastUpdate
      ) || 0;

    const deviceStatus = resolveStatus(device, {});
    const isExpiredDevice = deviceStatus === 'expired';
    const deviceLiveData =
      isExpiredDevice
        ? device?.liveData || {}
        : liveData && Object.keys(liveData).length > 0
          ? liveData
          : device?.liveData || {};
    const socketById = getDeviceSocketData?.(device?._id) || {};
    const socketByImei = getDeviceSocketData?.(device?.imei) || {};
    const socketByIdTs =
      toTimestampMs(
        socketById.packetReceivedAt ??
          socketById.lastSocketUpdate ??
          socketById.packetTime ??
          socketById.serverReceivedAt ??
          socketById.receivedAt ??
          socketById.timestamp ??
          socketById.serverTime
      ) || 0;
    const socketByImeiTs =
      toTimestampMs(
        socketByImei.packetReceivedAt ??
          socketByImei.lastSocketUpdate ??
          socketByImei.packetTime ??
          socketByImei.serverReceivedAt ??
          socketByImei.receivedAt ??
          socketByImei.timestamp ??
          socketByImei.serverTime
      ) || 0;
    const socketData = socketByImeiTs > socketByIdTs ? socketByImei : socketById;
    const now = Date.now();

    const effectiveSocketData = isExpiredDevice ? {} : socketData;
    const socketTs = getPanelTimestamp(effectiveSocketData);
    const dbTs = Math.max(
      getPanelTimestamp(deviceLiveData),
      getPanelTimestamp(device?.deviceLive),
      getPanelTimestamp(device?.liveSnapshot),
      getPanelTimestamp(device?.liveData),
      toTimestampMs(device?.lastDataReceivedAt) || 0
    );

    const freshestTs = Math.max(socketTs, dbTs);
    const age = freshestTs ? now - freshestTs : Infinity;
    const hasFreshSignal = freshestTs > 0 && age <= STALE_STATUS_DURATION_MS;

    const mergedLiveSource = isExpiredDevice
      ? {}
      : {
          ...(device?.liveData || {}),
          ...(device?.deviceLive || {}),
          ...(device?.liveSnapshot || {}),
          ...(deviceLiveData || {}),
          ...(effectiveSocketData || {}),
        };
    const resolvedDevice = {
      ...(device || {}),
      liveData: {
        ...(device?.liveData || {}),
        ...(mergedLiveSource || {}),
      },
      currentLocation: {
        ...(device?.currentLocation || {}),
        ...(mergedLiveSource || {}),
      },
    };
    const finalData = socketTs >= dbTs ? effectiveSocketData : deviceLiveData;
    const snapshot = getResolvedLiveSnapshot(resolvedDevice, mergedLiveSource);
    const status = isExpiredDevice ? 'expired' : resolveStatus(resolvedDevice, mergedLiveSource);
    const lat = snapshot.latitude ?? finalData.latitude ?? finalData.lat ?? device.currentLocation?.latitude;
    const lng = snapshot.longitude ?? finalData.longitude ?? finalData.lng ?? device.currentLocation?.longitude;
    const rawSpeed = Number(snapshot.speed ?? finalData.speed ?? 0);
    const accSignal = normalizeAcc(snapshot.accStatus ?? finalData.accStatus);
    const displaySpeed = isExpiredDevice ? 0 : rawSpeed;
    const rawIdleSinceMs = Number(
      finalData.idleSinceMs ??
      finalData.idleSince ??
      deviceLiveData.idleSinceMs ??
      deviceLiveData.idleSince ??
      device?.liveData?.idleSinceMs ??
      device?.liveData?.idleSince ??
      0
    );
    const prevTracking = trackingStateRef.current;
    const tracking = resolveTrackingState({
      prev: prevTracking,
      speed: displaySpeed,
      accStatus: isExpiredDevice
        ? false
        : snapshot.accStatus ?? finalData.accStatus ?? finalData.acc ?? finalData.ignition ?? null,
      timestampMs: freshestTs || now,
      latitude: Number.isFinite(Number(lat)) ? Number(lat) : null,
      longitude: Number.isFinite(Number(lng)) ? Number(lng) : null,
      heading: finalData.heading ?? finalData.course ?? finalData.bearing ?? 0,
      vehicleStatus: finalData.vehicleStatus ?? finalData.vehicle_status ?? finalData.status ?? status,
      movementState: finalData.movementState ?? finalData.movement_state ?? null,
    });
    const resolvedStatus = status;
    const prevStatus = String(prevTracking?.vehicleStatus || "").toLowerCase();
    const prevStatusSinceMs = Number(prevTracking?.statusSinceMs || 0);
    let statusSinceMs = hasFreshSignal ? (freshestTs || now) : null;

    if (!hasFreshSignal) {
      statusSinceMs = null;
    } else if (String(resolvedStatus || "").toLowerCase() === prevStatus && prevStatusSinceMs > 0) {
      statusSinceMs = prevStatusSinceMs;
    } else if (resolvedStatus === 'stopped') {
      statusSinceMs = Number(tracking?.lastZeroSpeedAtMs || 0) || freshestTs || now;
    } else if (resolvedStatus === 'idle') {
      statusSinceMs = rawIdleSinceMs > 0 ? rawIdleSinceMs : (freshestTs || now);
    } else if (resolvedStatus === 'running') {
      statusSinceMs = Number(tracking?.lastMovingAtMs || 0) || freshestTs || now;
    }

    trackingStateRef.current = {
      ...tracking,
      vehicleStatus: resolvedStatus,
      statusSinceMs,
    };

    return {
      speed: displaySpeed,
      lat,
      lng,
      heading: finalData.heading ?? finalData.course ?? finalData.bearing ?? 0,
      accStatus: accSignal,
      vehicleStatus: resolvedStatus,
      fuelLevel: finalData.fuelLevel ?? null,
      battery: finalData.battery ?? finalData.batteryVoltage ?? null,
      satellites: finalData.satellites ?? null,
      rssi: finalData.rssi ?? null,
      temperature: finalData.temperature ?? null,
      address: formatAddress(finalData.address) ?? null,
      deviceTime:
        pickFirstTimeValue(
          finalData.deviceTime,
          finalData.correctedTime,
          finalData.timestamp,
          deviceLiveData.deviceTime,
          deviceLiveData.correctedTime,
          deviceLiveData.timestamp
        ) ?? null,
      serverTime:
        pickFirstTimeValue(
          finalData.serverTime,
          finalData.serverReceivedAt,
          finalData.receivedAt,
          finalData.packetReceivedAt,
          deviceLiveData.serverTime,
          deviceLiveData.serverReceivedAt,
          deviceLiveData.receivedAt,
          freshestTs
        ) ?? null,
      correctedTime:
        finalData.correctedTime ??
        deviceLiveData.correctedTime ??
        freshestTs,
      isDrifted: Boolean(finalData.isDrifted ?? deviceLiveData.isDrifted),
      driftMs: Number(finalData.driftMs ?? deviceLiveData.driftMs ?? 0) || 0,
      driftSeconds: Number(finalData.driftSeconds ?? deviceLiveData.driftSeconds ?? 0) || 0,
      source: isExpiredDevice ? 'database' : socketTs >= dbTs ? 'realtime' : 'database',
      dataAge: age,
      hasFreshSignal,
      isFresh: null,
      freshness: null,
      timestamp: freshestTs,
      statusSinceMs,
    };
  }, [device, liveData, getDeviceSocketData, refreshCounter]);

  useEffect(() => {
    if (!TIME_DEBUG_UI_ENABLED || !device?._id) return;

    console.log("[TIME_DEBUG][RightInfoPanel.render]", {
      deviceId: String(device._id || ""),
      imei: String(device?.imei || ""),
      source: computedData.source,
      vehicleStatus: computedData.vehicleStatus,
      deviceTime: computedData.deviceTime || null,
      serverTime: computedData.serverTime || null,
      correctedTime: computedData.correctedTime || null,
      timestamp: computedData.timestamp || null,
      driftMs: computedData.driftMs || 0,
      driftSeconds: computedData.driftSeconds || 0,
    });
  }, [
    device?._id,
    device?.imei,
    computedData.source,
    computedData.vehicleStatus,
    computedData.deviceTime,
    computedData.serverTime,
    computedData.correctedTime,
    computedData.timestamp,
    computedData.driftMs,
    computedData.driftSeconds,
  ]);

  // Address resolution
  useEffect(() => {
    const incomingAddress = normalizeAddressText(computedData.address);
    if (incomingAddress) {
      setResolvedAddress(incomingAddress);
    }
  }, [computedData.address]);

  // Fetch geocode address
  useEffect(() => {
    const lat = Number(computedData.lat);
    const lng = Number(computedData.lng);
    if (!device?._id || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    const currentPos = { lat, lng };
    const deviceKey = String(device._id);
    const now = Date.now();

    const distanceMeters = (a, b) => {
      if (!a || !b) return true;
      const R = 6371000;
      const dLat = (b.lat - a.lat) * Math.PI / 180;
      const dLng = (b.lng - a.lng) * Math.PI / 180;
      const x = dLat * dLat + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * dLng * dLng;
      return Math.sqrt(x) * R;
    };

    const prev = geocodeMetaRef.current;
    const moved = prev.key !== deviceKey ? Infinity : distanceMeters(
      { lat: prev.lat, lng: prev.lng },
      currentPos
    );
    const elapsed = prev.key !== deviceKey ? Infinity : now - prev.at;

    if (moved < 20 && elapsed < 45000) return;

    let cancelled = false;

    const fetchAddress = async () => {
      setIsLoadingAddress(true);

      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://app.krush.in/api';

        const response = await fetch(
          `${baseUrl}/devices/${device._id}/address?lat=${currentPos.lat}&lng=${currentPos.lng}`,
          { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } }
        );

        if (!response.ok) throw new Error('Address fetch failed');
        const data = await response.json();

        if (!cancelled) {
          const normalized =
            normalizeAddressText(data.address) ||
            normalizeAddressText(data.address?.full) ||
            normalizeAddressText(data.address?.short);
          if (normalized) setResolvedAddress(normalized);
          geocodeMetaRef.current = {
            key: deviceKey,
            lat: currentPos.lat,
            lng: currentPos.lng,
            at: Date.now(),
          };
        }
      } catch (err) {
        // handle error silently
      } finally {
        if (!cancelled) setIsLoadingAddress(false);
      }
    };

    const timeoutId = setTimeout(fetchAddress, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [device?._id, computedData.lat, computedData.lng]);

  const hasNeverReportedData = useMemo(() => {
    const statusKey = String(computedData?.vehicleStatus || "").trim().toLowerCase();
    if (statusKey === "no_data" || statusKey === "nodata") return true;

    const latestKnownPacketMs = Math.max(
      toTimestampMs(computedData?.timestamp) || 0,
      toTimestampMs(computedData?.deviceTime) || 0,
      toTimestampMs(computedData?.serverTime) || 0,
      toTimestampMs(computedData?.correctedTime) || 0,
      toTimestampMs(device?.lastDataReceivedAt) || 0,
      toTimestampMs(device?.liveData?.timestamp) || 0,
      toTimestampMs(device?.deviceLive?.timestamp) || 0,
      toTimestampMs(device?.liveSnapshot?.timestamp) || 0,
    );

    return latestKnownPacketMs <= 0;
  }, [
    computedData?.correctedTime,
    computedData?.deviceTime,
    computedData?.serverTime,
    computedData?.timestamp,
    computedData?.vehicleStatus,
    device?.deviceLive?.timestamp,
    device?.lastDataReceivedAt,
    device?.liveData?.timestamp,
    device?.liveSnapshot?.timestamp,
  ]);

  // Fetch daily stats
  useEffect(() => {
    if (!device?._id) return;
    if (hasNeverReportedData) {
      setDailyStats(EMPTY_DAILY_STATS);
      return;
    }
    
    const fetchDailyStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const today = new Date().toISOString().split('T')[0];
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://app.krush.in/api'}/history/${device._id}/summary?date=${today}`,
          { 
            headers: { 'Authorization': `Bearer ${token}`, 'x-auth-token': token },
            signal: AbortSignal.timeout(5000)
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.hasData && data.summary) {
            setDailyStats({
              distance: Math.max(0, data.summary.totalDistance || 0),
              runningTime: Math.max(0, data.summary.movingTime || 0),
              idleTime: data.summary.idleTime || 0,
              stopTime: data.summary.stopTime || 0,
              inactiveTime: data.summary.inactiveTime || 0,
              maxSpeed: data.summary.maxSpeed || 0,
              avgSpeed: data.summary.avgSpeed || 0,
              fuelConsumption: data.summary.fuelConsumption || 0,
            });
          } else {
            setDailyStats(EMPTY_DAILY_STATS);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch daily stats:', err);
        }
        setDailyStats(EMPTY_DAILY_STATS);
      }
    };
    
    fetchDailyStats();
    const interval = setInterval(fetchDailyStats, 60000);
    return () => clearInterval(interval);
  }, [device?._id, hasNeverReportedData]);

  // Helper functions
  const getStatusColor = useCallback(() => {
    const status = computedData.vehicleStatus;
    if (status === 'running') return '#16a34a';
    if (status === 'idle') return '#ca8a04';
    if (status === 'stopped') return '#dc2626';
    if (status === 'inactive') return '#0ea5e9';
    if (status === 'expired') return '#3b82f6';
    return '#6b7280';
  }, [computedData.vehicleStatus]);

  const getElapsedTime = useCallback(() => {
    if (computedData.hasFreshSignal === false) return 'No recent data';
    const updateTime =
      computedData.statusSinceMs ||
      computedData.timestamp ||
      lastUpdateTime ||
      (device?.lastDataReceivedAt ? new Date(device.lastDataReceivedAt).getTime() : null);

    if (!updateTime) return 'No signal';

    const secondsAgo = Math.floor((Date.now() - updateTime) / 1000);
    const safeSeconds = Math.max(0, secondsAgo);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [computedData.statusSinceMs, computedData.timestamp, lastUpdateTime, device?.lastDataReceivedAt]);

const statusColor = getStatusColor();
const isNotReporting = computedData.vehicleStatus === "inactive";
const isNoDataDevice = hasNeverReportedData;
const hasDevice = Boolean(device?._id || device?.id);
  const lastSeenLabel = useMemo(() => {
    if (isNoDataDevice) return 'No data';
    if (computedData.hasFreshSignal === false) return 'Last seen earlier';
    const ts = computedData.timestamp || lastUpdateTime;
    if (!ts) return 'No recent data';
    const diff = Math.max(0, Date.now() - ts);
    if (diff < 15000) return 'Just now';
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return `${Math.floor(diff / 1000)} sec ago`;
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    return formatDisplayTime(ts);
  }, [computedData.hasFreshSignal, computedData.timestamp, isNoDataDevice, lastUpdateTime]);
  const directionLabel = useMemo(() => formatCompassDirection(computedData.heading), [computedData.heading]);
  const renderedDeviceTime = useMemo(() => {
    if (isNoDataDevice) return null;
    if (computedData.isDrifted) {
      return (
        pickFirstTimeValue(
          computedData.correctedTime,
          computedData.serverTime,
          computedData.deviceTime
        ) ?? null
      );
    }
    return (
      pickFirstTimeValue(
        computedData.deviceTime,
        computedData.correctedTime,
        computedData.serverTime
      ) ?? null
    );
  }, [
    computedData.correctedTime,
    computedData.deviceTime,
    computedData.isDrifted,
    computedData.serverTime,
    isNoDataDevice,
  ]);
  const deviceDisplayName = device?.vehicleNumber || device?.name || device?.imei || 'Vehicle';
  const modelDisplayName = device?.model || device?.deviceModel || device?.vehicleType || '';
  const copyText = useCallback(async (value, successMessage) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      showStatus('success', successMessage);
    } catch (error) {
      showStatus('error', 'Copy failed');
    }
  }, [showStatus]);
  const sectionCardSx = {
    bgcolor: '#ffffff',
    border: '1px solid rgba(226,232,240,0.95)',
    borderRadius: isMobile ? 3 : 3.5,
    boxShadow: '0 10px 28px rgba(15,23,42,0.07)',
  };
  const bottomTabs = [
    { key: 'info', label: 'Vehicle Info' },
    { key: 'telemetry', label: 'Telemetry' },
    { key: 'actions', label: 'Actions' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'history', label: 'History' },
    { key: 'geofence', label: 'Geofence' },
    { key: 'landmark', label: 'Landmark' },
  ];
  const compactText = isMobile ? '0.74rem' : (isCompactDesktop ? '0.76rem' : '0.84rem');
  const compactLabel = isMobile ? '0.68rem' : (isCompactDesktop ? '0.7rem' : '0.78rem');
  const compactTitle = isMobile ? '0.82rem' : (isCompactDesktop ? '0.86rem' : '0.96rem');
  const vehicleInfoColumns = [
    [
      { label: 'Vehicle Number', value: deviceDisplayName || 'NA', icon: <DirectionsCarOutlinedIcon sx={{ fontSize: 18 }} /> },
      { label: 'Driver', value: assignedDriver.name || 'Not Assigned', icon: <InfoIcon sx={{ fontSize: 18 }} /> },
      { label: 'IMEI', value: device?.imei || 'NA', icon: <EngineIcon sx={{ fontSize: 18 }} /> },
      { label: 'Device Model', value: modelDisplayName || 'NA', icon: <MemoryIcon sx={{ fontSize: 18 }} /> },
      { label: 'Device ID', value: String(device?._id || device?.id || 'NA'), icon: <InfoIcon sx={{ fontSize: 18 }} /> },
      { label: 'Sim Number', value: device?.msisdn || device?.simNumber || assignedDriver.mobile || 'NA', icon: <ShareIcon sx={{ fontSize: 18 }} /> },
    ],
    [
      { label: 'Registration Number', value: device?.vehicleNumber || 'NA', icon: <DirectionsCarOutlinedIcon sx={{ fontSize: 18 }} /> },
      { label: 'Owner Name', value: currentUser?.companyName || currentUser?.username || 'NA', icon: <InfoIcon sx={{ fontSize: 18 }} /> },
      { label: 'Vehicle Type', value: device?.vehicleType || 'NA', icon: <RouteIcon sx={{ fontSize: 18 }} /> },
      { label: 'Model Year', value: device?.modelYear || device?.year || 'NA', icon: <ClockIcon sx={{ fontSize: 18 }} /> },
      { label: 'Insurance Validity', value: device?.insuranceValidity || 'NA', icon: <LandmarkIcon sx={{ fontSize: 18 }} /> },
      { label: 'Fitness Validity', value: device?.fitnessValidity || 'NA', icon: <LandmarkIcon sx={{ fontSize: 18 }} /> },
    ],
  ];
  const telemetryHighlights = [
    { label: 'Speed', value: isNoDataDevice ? 'NA' : `${Math.max(Math.round(computedData.speed || 0), 0)} km/h`, icon: <SpeedIcon sx={{ fontSize: 18 }} /> },
    { label: 'Direction', value: directionLabel, icon: <NavigationIcon sx={{ fontSize: 18 }} /> },
    { label: 'Running Time', value: getElapsedTime(), icon: <ClockIcon sx={{ fontSize: 18 }} /> },
    { label: 'GPS', value: isNoDataDevice ? 'No Data' : (computedData.hasFreshSignal === false ? 'No Signal' : 'Connected'), icon: <SatelliteIcon sx={{ fontSize: 18 }} /> },
  ];
  const telemetryTableRows = [
    { label: 'Ignition', value: isNoDataDevice ? 'NA' : (computedData.accStatus ? 'ON' : 'OFF'), icon: <PowerOffIcon sx={{ fontSize: 18 }} />, tone: computedData.accStatus ? '#16a34a' : '#64748b' },
    { label: 'Battery', value: isNoDataDevice ? 'NA' : (computedData.battery ?? 'NA'), icon: <BatteryIcon sx={{ fontSize: 18 }} />, tone: '#2563eb' },
    { label: 'Signal', value: isNoDataDevice ? 'NA' : (computedData.rssi ?? 'NA'), icon: <SignalIcon sx={{ fontSize: 18 }} />, tone: '#16a34a' },
    { label: 'Satellites', value: isNoDataDevice ? 'NA' : (computedData.satellites ?? 'NA'), icon: <SatelliteIcon sx={{ fontSize: 18 }} />, tone: '#7c3aed' },
    { label: 'Status', value: isNotReporting ? 'No Reporting' : formatStatusLabel(computedData.vehicleStatus), icon: <InfoIcon sx={{ fontSize: 18 }} />, tone: statusColor },
    { label: 'Device Time', value: isNoDataDevice ? 'NA' : formatDisplayTime(renderedDeviceTime), icon: <ClockIcon sx={{ fontSize: 18 }} />, tone: '#0f172a' },
    { label: 'Server Time', value: isNoDataDevice ? 'NA' : formatDisplayTime(computedData.serverTime), icon: <RefreshIcon sx={{ fontSize: 18 }} />, tone: '#0f172a' },
    { label: 'Last Update', value: lastSeenLabel, icon: <RefreshIcon sx={{ fontSize: 18 }} />, tone: '#0f172a' },
  ];
  const actionCards = [
    { key: 'center', label: 'Center Map', icon: <MyLocationIcon sx={{ fontSize: 20 }} />, onClick: () => onFitBounds?.() },
    { key: 'history', label: 'History', icon: <HistoryMapIcon sx={{ fontSize: 20 }} />, onClick: () => onShowHistory?.(device) },
    { key: 'driver', label: 'Assign Driver', icon: <Avatar sx={{ width: 20, height: 20, bgcolor: 'transparent', color: '#2563eb', fontSize: 18 }}>+</Avatar>, onClick: () => setAssignDriverOpen(true) },
    { key: 'command', label: 'Send Command', icon: <EngineIcon sx={{ fontSize: 20 }} />, onClick: () => handleCommand?.() },
    { key: 'more', label: 'More Details', icon: <MoreHorizIcon sx={{ fontSize: 20 }} />, onClick: () => onOpenReport?.(device) },
  ];

  const deviceNotifications = useMemo(() => {
    return (Array.isArray(notifications) ? notifications : [])
      .slice()
      .sort((a, b) =>
        new Date(b?.triggeredAt || b?.timestamp || 0).getTime() -
        new Date(a?.triggeredAt || a?.timestamp || 0).getTime()
      )
      .slice(0, 80);
  }, [notifications]);

  const geofenceRows = useMemo(() => {
    return (Array.isArray(sidebarGeofences) ? sidebarGeofences : []).map((item, index) => ({
      id: String(item?.id || item?._id || `geofence-${index}`),
      _id: item?._id || item?.id || null,
      name: item?.name || `Geofence ${index + 1}`,
      type: item?.type || (item?.radius ? "circle" : "polygon"),
      category: item?.category || "",
      locationLabel: item?.locationLabel || "",
      radiusText: item?.radiusText || "",
      assignedCount: Number(item?.assignedCount || item?.assignedDeviceIds?.length || 0),
      assignedDeviceIds: Array.isArray(item?.assignedDeviceIds) ? item.assignedDeviceIds : [],
      raw: item,
    }));
  }, [sidebarGeofences]);

  const landmarkRows = useMemo(() => {
    return (Array.isArray(sidebarLandmarks) ? sidebarLandmarks : []).map((item, index) => ({
      ...item,
      id: String(item?.id || item?._id || `landmark-${index}`),
      _id: item?._id || item?.id || null,
      name: item?.name || `Landmark ${index + 1}`,
      category: item?.category || "others",
      address: item?.address || "",
      description: item?.description || "",
      latitude: Number(item?.latitude ?? item?.lat),
      longitude: Number(item?.longitude ?? item?.lng),
      radius: Number(item?.radius ?? item?.radiusMeters ?? 30),
      radiusText: Number(item?.radius ?? item?.radiusMeters ?? 30).toFixed(2),
      assignedCount: Number(item?.assignedCount ?? (Array.isArray(item?.assignedDevices) ? item.assignedDevices.length : 0)),
      raw: item,
    }));
  }, [sidebarLandmarks]);

  const resolveLandmarkContextCoordinates = useCallback(() => {
    const sourceDevice = currentContextDevice || device;
    const live = sourceDevice?.liveData || {};
    const lat = Number(
      live?.latitude ??
      live?.lat ??
      sourceDevice?.currentLocation?.latitude ??
      sourceDevice?.liveData?.latitude ??
      sourceDevice?.latitude ??
      sourceDevice?.lat
    );
    const lng = Number(
      live?.longitude ??
      live?.lng ??
      sourceDevice?.currentLocation?.longitude ??
      sourceDevice?.liveData?.longitude ??
      sourceDevice?.longitude ??
      sourceDevice?.lng
    );
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }, [currentContextDevice, device]);

  const reverseGeocodeCoordinates = useCallback(async (lat, lng) => {
    const response = await api.get("/geocode/reverse", {
      params: { lat, lon: lng },
    });
    return String(response?.data?.address || `${lat}, ${lng}`).trim();
  }, []);

  const openCreateLandmarkForm = useCallback(async () => {
    setActiveTab("landmark");
    setLandmarkMode("create");
    setLandmarkEditingId(null);
    setLandmarkForm({
      name: "",
      category: "others",
      address: "",
      description: "",
      latitude: null,
      longitude: null,
      radius: 30,
    });
    setLandmarkLocationQuery("");
    setLandmarkFormError("");
    setLandmarkPlacementActive(true);
    onLandmarkDraftChange?.(null);
    setLandmarkFormOpen(true);

    const coords = resolveLandmarkContextCoordinates();
    if (!coords) return;
    setLandmarkLocationLoading(true);
    try {
      const address = await reverseGeocodeCoordinates(coords.lat, coords.lng);
      setLandmarkForm((prev) => ({
        ...prev,
        address,
        description: prev.description || address,
        latitude: coords.lat,
        longitude: coords.lng,
      }));
      setLandmarkLocationQuery(address);
      onLandmarkDraftChange?.({
        latitude: coords.lat,
        longitude: coords.lng,
        lat: coords.lat,
        lng: coords.lng,
        radius: 30,
      });
    } catch (_) {
      setLandmarkForm((prev) => ({ ...prev, latitude: coords.lat, longitude: coords.lng }));
    } finally {
      setLandmarkLocationLoading(false);
    }
  }, [onLandmarkDraftChange, resolveLandmarkContextCoordinates, reverseGeocodeCoordinates]);

  const openEditLandmarkForm = useCallback((landmark) => {
    if (!landmark) return;
    const latitude = Number(landmark?.latitude ?? landmark?.lat);
    const longitude = Number(landmark?.longitude ?? landmark?.lng);
    const radius = Number(landmark?.radius ?? landmark?.radiusMeters ?? 30);
    setActiveTab("landmark");
    setLandmarkMode("edit");
    setLandmarkEditingId(String(landmark?._id || landmark?.id || ""));
    setLandmarkPlacementActive(false);
    setLandmarkForm({
      name: String(landmark?.name || "").trim(),
      category: String(landmark?.category || "others").trim() || "others",
      address: String(landmark?.address || "").trim(),
      description: String(landmark?.description || "").trim(),
      latitude,
      longitude,
      radius,
    });
    setLandmarkLocationQuery(String(landmark?.address || "").trim());
    setLandmarkFormError("");
    setLandmarkFormOpen(true);
    onLandmarkDraftChange?.({
      latitude,
      longitude,
      lat: latitude,
      lng: longitude,
      radius,
    });
  }, [onLandmarkDraftChange]);

  const closeLandmarkForm = useCallback(() => {
    setLandmarkFormOpen(false);
    setLandmarkMode("create");
    setLandmarkEditingId(null);
    setLandmarkPlacementActive(false);
    setLandmarkFormError("");
    onLandmarkDraftChange?.(null);
  }, [onLandmarkDraftChange]);

  const handleUseCurrentLandmarkLocation = useCallback(async () => {
    const coords = resolveLandmarkContextCoordinates();
    if (!coords) {
      setLandmarkFormError("Current vehicle location not available.");
      return;
    }
    setLandmarkLocationLoading(true);
    setLandmarkFormError("");
    try {
      const address = await reverseGeocodeCoordinates(coords.lat, coords.lng);
      setLandmarkForm((prev) => ({
        ...prev,
        address,
        description: prev.description || address,
        latitude: coords.lat,
        longitude: coords.lng,
      }));
      setLandmarkLocationQuery(address);
      onLandmarkDraftChange?.((currentDraft) => ({
        ...(currentDraft || {}),
        latitude: coords.lat,
        longitude: coords.lng,
        lat: coords.lat,
        lng: coords.lng,
        radius: Number(currentDraft?.radius ?? landmarkForm?.radius ?? 30),
      }));
    } catch (error) {
      setLandmarkFormError(error?.message || "Unable to detect current location.");
    } finally {
      setLandmarkLocationLoading(false);
    }
  }, [landmarkForm?.radius, onLandmarkDraftChange, resolveLandmarkContextCoordinates, reverseGeocodeCoordinates]);

  const handleLandmarkSubmit = useCallback(async () => {
    const name = String(landmarkForm?.name || "").trim();
    const latitude = Number(landmarkDraftValue?.latitude ?? landmarkForm?.latitude);
    const longitude = Number(landmarkDraftValue?.longitude ?? landmarkForm?.longitude);
    const radius = Math.max(0, Number(landmarkDraftValue?.radius ?? landmarkForm?.radius ?? 30));
    if (!name) {
      setLandmarkFormError("Landmark name is required.");
      return;
    }
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setLandmarkFormError("Landmark location is required. Use current location or draw circle on map.");
      return;
    }
    const payload = {
      name,
      category: String(landmarkForm?.category || "others").trim() || "others",
      address: String(landmarkForm?.address || landmarkLocationQuery || "").trim(),
      description: String(landmarkForm?.description || "").trim(),
      latitude,
      longitude,
      radius,
    };
    try {
      const savedLandmark =
        landmarkMode === "edit" && landmarkEditingId
          ? await onUpdateSidebarLandmark?.({ landmarkId: landmarkEditingId, ...payload })
          : await onCreateSidebarLandmark?.(payload);
      if (savedLandmark) onSelectLandmark?.(savedLandmark);
      closeLandmarkForm();
    } catch (error) {
      setLandmarkFormError(error?.message || "Failed to save landmark.");
    }
  }, [
    closeLandmarkForm,
    landmarkDraftValue,
    landmarkEditingId,
    landmarkForm,
    landmarkLocationQuery,
    landmarkMode,
    onCreateSidebarLandmark,
    onSelectLandmark,
    onUpdateSidebarLandmark,
  ]);

  const availableDrivers = useMemo(() => {
    const rows = Array.isArray(driversData) ? driversData : driversData?.data || [];
    const q = String(driverSearch || "").trim().toLowerCase();
    const normalized = rows.map((driver) => ({
      ...driver,
      label: driver?.username || driver?.name || "Driver",
      mobile: driver?.mobileNo || driver?.mobile || "",
      id: String(driver?._id || driver?.id || ""),
    }));

    if (!q) return normalized;

    return normalized
      .filter((driver) =>
        driver.label.toLowerCase().includes(q) ||
        String(driver.mobile || "").includes(q)
      )
      .sort((a, b) => {
        const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        return aStarts - bStarts || a.label.localeCompare(b.label);
      });
  }, [driverSearch, driversData]);

  const normalizedDriverAssignmentDevices = useMemo(() => {
    if (Array.isArray(allDevicesForDriverAssign)) return allDevicesForDriverAssign;
    if (Array.isArray(allDevicesForDriverAssign?.data)) return allDevicesForDriverAssign.data;
    return [];
  }, [allDevicesForDriverAssign]);

  const handleAssignDriver = useCallback(async (driver) => {
    if (!device?._id || !driver?.id) return;

    const conflictingDevice = normalizedDriverAssignmentDevices.find((row) => {
      const rowId = String(row?._id || row?.id || "");
      const rowDriverId = String(row?.driver?.driverId || row?.driverId || "");
      return rowId && rowId !== String(device?._id || "") && rowDriverId === String(driver.id);
    });

    if (conflictingDevice) {
      showStatus(
        "error",
        `This driver is already assigned to ${conflictingDevice?.name || conflictingDevice?.imei || "another device"}.`
      );
      return;
    }

    try {
      const response = await updateDevice({
        id: device._id,
        driverId: driver.id,
        driverName: driver.label,
        driverContact: driver.mobile || "",
        driver: {
          driverId: driver.id,
          driverName: driver.label,
          contactNumber: driver.mobile || "",
        },
      }).unwrap();

      const updatedDevice = response?.device || response?.data || response;
      const nextDevice = {
        ...device,
        ...updatedDevice,
        driver: {
          ...(device?.driver || {}),
          ...(updatedDevice?.driver || {}),
          driverId:
            updatedDevice?.driver?.driverId ||
            updatedDevice?.driverId ||
            driver.id,
          driverName:
            updatedDevice?.driver?.driverName ||
            updatedDevice?.driverName ||
            driver.label,
          contactNumber:
            updatedDevice?.driver?.contactNumber ||
            updatedDevice?.driverContact ||
            driver.mobile ||
            "",
        },
      };

      setAssignedDriver({
        id: String(
          nextDevice?.driverId ||
          nextDevice?.driver?.driverId ||
          driver.id
        ),
        name:
          nextDevice?.driver?.driverName ||
          nextDevice?.driverName ||
          driver.label,
        mobile:
          nextDevice?.driver?.contactNumber ||
          nextDevice?.driverContact ||
          driver.mobile ||
          "",
      });
      if (typeof onDeviceUpdated === "function") {
        onDeviceUpdated(nextDevice);
      }
      setAssignDriverOpen(false);
      setDriverSearch("");
      showStatus("success", "Driver assigned successfully");
    } catch (error) {
      showStatus("error", error?.data?.msg || error?.data?.error || "Failed to assign driver");
    }
  }, [device, normalizedDriverAssignmentDevices, onDeviceUpdated, showStatus, updateDevice]);

  const vehicleIconSrc = useMemo(() => {
    if (!hasDevice || typeof getVehicleIcon !== 'function') return null;
    const iconKey = device?.vehicleType ?? device?.deviceIcon ?? device?.vehicleIcon ?? device?.icon ?? 'car';
    return getVehicleIcon(iconKey);
  }, [device, getVehicleIcon, hasDevice]);
  const mapTypes = ['roadmap', 'satellite', 'terrain', 'hybrid'];
  const cycleMapType = () => {
    if (!setMapType) return;
    const currentIndex = mapTypes.indexOf(mapType);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % mapTypes.length;
    setMapType(mapTypes[nextIndex]);
  };
  const actionRailButtons = [
    {
      key: 'locate',
      label: 'Center',
      icon: <MyLocationIcon sx={{ fontSize: 18 }} />,
      onClick: () => onLocateMe?.(),
    },
    {
      key: 'follow',
      label: 'Navigation',
      icon: <NavigationIcon sx={{ fontSize: 18, transform: 'rotate(18deg)' }} />,
      active: followMode,
      disabled: !hasSelectedDevice,
      onClick: () => hasSelectedDevice && setFollowMode?.(!followMode),
    },
    {
      key: 'fit',
      label: 'Zoom Fit',
      icon: <FitBoundsIcon sx={{ fontSize: 18 }} />,
      onClick: () => onFitBounds?.(),
    },
    {
      key: 'map-type',
      label: 'Layers',
      icon: <MapTypeIcon sx={{ fontSize: 18 }} />,
      active: mapType !== 'roadmap',
      onClick: cycleMapType,
    },
    {
      key: 'traffic-toggle',
      label: 'Traffic',
      icon: <TrafficIcon sx={{ fontSize: 18 }} />,
      active: showTraffic,
      onClick: () => setShowTraffic?.(!showTraffic),
    },
  ].filter((action) => !action.hidden);

  const toolbarLabelVisible = !isMobile || !isVerySmallViewport;
  const renderToolbarButton = (action) => (
    <Tooltip key={action.key} title={action.label} placement="left" arrow>
      <span>
        <Box
          component="button"
          onClick={action.onClick}
          disabled={action.disabled}
          sx={{
            width: isMobile ? (toolbarLabelVisible ? 72 : 44) : (isCompactDesktop ? 58 : 66),
            minHeight: isMobile ? (toolbarLabelVisible ? 62 : 44) : (isCompactDesktop ? 58 : 68),
            px: toolbarLabelVisible ? 0.8 : 0,
            py: toolbarLabelVisible ? 0.7 : 0,
            borderRadius: isCompactDesktop && !isMobile ? '14px' : '16px',
            border: action.active ? '1px solid rgba(59,130,246,0.5)' : '1px solid rgba(226,232,240,0.98)',
            bgcolor: 'rgba(255,255,255,0.98)',
            color: '#2563eb',
            opacity: action.disabled ? 0.42 : 1,
            boxShadow: action.active
              ? '0 0 0 4px rgba(59,130,246,0.12), 0 14px 28px rgba(37,99,235,0.16)'
              : '0 12px 28px rgba(15,23,42,0.08)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: toolbarLabelVisible ? 0.45 : 0,
            cursor: action.disabled ? 'not-allowed' : 'pointer',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
            '&:hover': action.disabled ? {} : {
              transform: 'translateY(-1px)',
              borderColor: 'rgba(147,197,253,0.95)',
              boxShadow: action.active
                ? '0 0 0 4px rgba(59,130,246,0.14), 0 16px 30px rgba(37,99,235,0.18)'
                : '0 14px 30px rgba(15,23,42,0.1)',
            },
          }}
        >
          {React.isValidElement(action.icon)
            ? React.cloneElement(action.icon, {
                sx: {
                  ...(action.icon.props?.sx || {}),
                  fontSize: isCompactDesktop && !isMobile ? 18 : 20,
                  color: '#2563eb',
                },
              })
            : action.icon}
          {toolbarLabelVisible ? (
            <Typography
              sx={{
                fontSize: isCompactDesktop && !isMobile ? '0.6rem' : '0.66rem',
                lineHeight: 1.1,
                fontWeight: 700,
                color: '#334155',
                textAlign: 'center',
                letterSpacing: '-0.01em',
              }}
            >
              {action.label}
            </Typography>
          ) : null}
        </Box>
      </span>
    </Tooltip>
  );
  if (!isOpen) return null;

  return (
    <Box
      ref={panelRef}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        pointerEvents: 'none',
      }}
    >
      <Stack
        direction={isMobile ? 'row' : 'column'}
        spacing={isMobile ? 0.75 : (isCompactDesktop ? 0.6 : 0.95)}
        sx={{
          position: 'absolute',
          top: typeof topOffset === 'number' ? Math.max(topOffset - (isMobile ? 6 : 16), 8) : topOffset || '11px',
          right: isMobile ? 8 : 'var(--map-shell-padding, 14px)',
          alignItems: 'center',
          pt: 0,
          overflowX: isMobile ? 'auto' : 'visible',
          overflowY: isMobile ? 'hidden' : 'auto',
          maxHeight: isMobile ? 'none' : '100%',
          flexShrink: 0,
          pb: 0,
          pointerEvents: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {actionRailButtons.map((action) => renderToolbarButton(action))}

        <Stack spacing={isCompactDesktop ? 0.6 : 0.95} sx={{ mt: isCompactDesktop ? 0.15 : 0.2 }}>
          {[
            { key: 'zoom-in', label: 'Zoom +', icon: <PlusIcon sx={{ fontSize: 20 }} />, onClick: () => onZoomIn?.() },
            { key: 'zoom-out', label: 'Zoom -', icon: <MinusIcon sx={{ fontSize: 20 }} />, onClick: () => onZoomOut?.() },
          ].map((action) => renderToolbarButton(action))}
        </Stack>
      </Stack>

      <Paper
        elevation={10}
        sx={{
          position: 'absolute',
          left: isMobile
            ? '50%'
            : 'calc(var(--tracking-shell-left, 0px) + var(--map-shell-padding, 14px) + var(--sidebar-width, 0px) + var(--map-shell-gap, 14px))',
          right: isMobile ? 'auto' : 'calc(var(--map-shell-padding, 14px) + var(--right-rail-width, 48px) + var(--map-shell-gap, 14px))',
          bottom: isMobile ? 8 : 'var(--map-shell-padding, 14px)',
          width: isMobile ? 'min(42vw, 276px)' : 'auto',
          maxWidth: isMobile ? 'min(42vw, 276px)' : 'none',
          minWidth: isMobile ? 'min(42vw, 276px)' : 0,
          transform: isMobile ? 'translateX(-50%)' : 'none',
          height: collapsed
            ? '28px'
            : isMobile
              ? 'min(calc(100dvh - 210px), 54vh)'
              : desktopPanelHeight,
          maxHeight: collapsed
            ? '28px'
            : isMobile
              ? 'min(calc(100dvh - 210px), 54vh)'
              : desktopPanelHeight,
          bgcolor: '#f8fbff',
          borderRadius: isMobile ? '20px 20px 0 0' : '22px 22px 0 0',
          overflow: 'hidden',
          boxShadow: '0 20px 44px rgba(15,23,42,0.12)',
          border: '1px solid rgba(226,232,240,0.95)',
          display: hasDevice ? 'flex' : 'none',
          flexDirection: 'column',
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#ffffff',
            borderBottom: collapsed ? 'none' : '1px solid rgba(226,232,240,0.92)',
            minHeight: 28,
            position: 'relative',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{ color: '#1e3a8a' }}
          >
            <CollapseIcon sx={{ fontSize: 20, transform: collapsed ? 'rotate(180deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
          </IconButton>
          <Tooltip title="Close" placement="top" arrow>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 2, color: '#64748b' }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {!collapsed ? (
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: isCompactDesktop && !isMobile ? 0.85 : 1.1,
            pt: isCompactDesktop && !isMobile ? 0.75 : 1,
            pb: isCompactDesktop && !isMobile ? 0.8 : 1.05,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'linear-gradient(180deg, #f8fbff 0%, var(--logo-blue-tint, #e9f6ff) 100%)',
              borderRadius: '999px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, var(--logo-blue-soft, #78cfff) 0%, var(--logo-blue, #1d63c8) 100%)',
              borderRadius: '999px',
              border: '1px solid rgba(255, 255, 255, 0.65)',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(180deg, var(--logo-blue, #1d63c8) 0%, var(--logo-blue-hover, #114fae) 100%)',
            },
          }}
        >
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box sx={{ ...sectionCardSx, p: 0, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.2,
                    pt: 0.45,
                    borderBottom: '1px solid rgba(226,232,240,0.9)',
                    bgcolor: '#ffffff',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {bottomTabs.map((tab) => {
                    const active = activeTab === tab.key;
                    return (
                      <Button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        sx={{
                          minWidth: 0,
                          px: isCompactDesktop && !isMobile ? 1.4 : 1.8,
                          py: 1.05,
                          mr: 1,
                          borderRadius: 0,
                          borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
                          color: active ? '#2563eb' : '#64748b',
                          fontSize: isMobile ? '0.72rem' : (isCompactDesktop ? '0.78rem' : '0.88rem'),
                          fontWeight: active ? 800 : 700,
                          textTransform: 'none',
                          '&:hover': { bgcolor: 'transparent', color: '#2563eb' },
                        }}
                      >
                        {tab.label}
                      </Button>
                    );
                  })}
                </Box>

                {activeTab === 'info' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 1.15 }}>
                      {vehicleInfoColumns.map((column, columnIndex) => (
                        <Box key={`info-col-${columnIndex}`} sx={{ border: '1px solid rgba(226,232,240,0.92)', borderRadius: 2.8, overflow: 'hidden', bgcolor: '#fff' }}>
                          {column.map((row, rowIndex) => (
                            <Box
                              key={row.label}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '40px minmax(110px, 1fr) minmax(0, 1fr)',
                                gap: 1,
                                alignItems: 'center',
                                px: 1.15,
                                py: isCompactDesktop && !isMobile ? 0.95 : 1.15,
                                borderBottom: rowIndex === column.length - 1 ? 'none' : '1px solid rgba(241,245,249,0.95)',
                              }}
                            >
                              <Box sx={{ width: 28, height: 28, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#f8fbff', color: '#2563eb', border: '1px solid rgba(219,229,242,0.95)' }}>
                                {row.icon}
                              </Box>
                              <Typography sx={{ fontSize: compactLabel, color: '#64748b', fontWeight: 700 }}>
                                {row.label}
                              </Typography>
                              <Typography sx={{ fontSize: compactText, color: '#122033', fontWeight: 800, textAlign: 'right', wordBreak: 'break-word' }}>
                                {row.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {activeTab === 'telemetry' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25, display: 'grid', gap: 1 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 0.9 }}>
                      {telemetryHighlights.map((item) => (
                        <Box key={item.label} sx={{ border: '1px solid rgba(226,232,240,0.92)', borderRadius: 2.5, bgcolor: '#fbfdff', p: isCompactDesktop && !isMobile ? 0.9 : 1.05 }}>
                          <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#eef5ff', color: '#2563eb', mb: 0.75 }}>
                            {item.icon}
                          </Box>
                          <Typography sx={{ fontSize: compactLabel, color: '#64748b', fontWeight: 700 }}>{item.label}</Typography>
                          <Typography sx={{ mt: 0.25, fontSize: compactText, color: '#122033', fontWeight: 800 }}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ border: '1px solid rgba(226,232,240,0.92)', borderRadius: 2.8, overflow: 'hidden', bgcolor: '#fff' }}>
                      {telemetryTableRows.map((row, index) => (
                        <Box
                          key={row.label}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '36px minmax(88px, 1fr) minmax(0, 1fr)' : '40px minmax(140px, 0.9fr) minmax(0, 1fr)',
                            gap: 1,
                            alignItems: 'center',
                            px: 1.15,
                            py: isCompactDesktop && !isMobile ? 0.9 : 1.05,
                            borderBottom: index === telemetryTableRows.length - 1 ? 'none' : '1px solid rgba(241,245,249,0.95)',
                          }}
                        >
                          <Box sx={{ width: 28, height: 28, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#f8fbff', color: row.tone, border: '1px solid rgba(219,229,242,0.95)' }}>
                            {row.icon}
                          </Box>
                          <Typography sx={{ fontSize: compactLabel, color: '#64748b', fontWeight: 700 }}>
                            {row.label}
                          </Typography>
                          <Typography sx={{ fontSize: compactText, color: row.tone, fontWeight: 800, textAlign: 'right', wordBreak: 'break-word' }}>
                            {row.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {activeTab === 'actions' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25, display: 'grid', gap: 1 }}>
                    <Box sx={{ ...sectionCardSx, p: isCompactDesktop && !isMobile ? 0.9 : 1.1, boxShadow: 'none' }}>
                      <Typography sx={{ fontSize: compactTitle, color: '#122033', fontWeight: 800, mb: 0.6 }}>Location</Typography>
                      <Typography sx={{ fontSize: compactText, color: '#1f2937', lineHeight: 1.45 }}>
                        {isLoadingAddress ? 'Resolving address...' : resolvedAddress || normalizeAddressText(computedData.address) || 'Address unavailable'}
                      </Typography>
                      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontSize: compactLabel, color: '#2563eb', fontWeight: 800, wordBreak: 'break-all' }}>
                          {Number.isFinite(Number(computedData.lat)) && Number.isFinite(Number(computedData.lng))
                            ? `${Number(computedData.lat).toFixed(6)}, ${Number(computedData.lng).toFixed(6)}`
                            : 'NA'}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => copyText(
                            Number.isFinite(Number(computedData.lat)) && Number.isFinite(Number(computedData.lng))
                              ? `${Number(computedData.lat).toFixed(6)}, ${Number(computedData.lng).toFixed(6)}`
                              : '',
                            'Coordinates copied'
                          )}
                          sx={{ border: '1px solid rgba(226,232,240,0.95)', bgcolor: '#fff' }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))', gap: 0.9 }}>
                      {actionCards.map((action) => (
                        <Button
                          key={action.key}
                          onClick={action.onClick}
                          variant="outlined"
                          sx={{
                            minWidth: 0,
                            minHeight: isCompactDesktop && !isMobile ? 72 : 86,
                            p: isCompactDesktop && !isMobile ? 0.8 : 1,
                            borderRadius: 2.8,
                            borderColor: 'rgba(219,229,242,0.95)',
                            bgcolor: '#fff',
                            color: '#2563eb',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.65,
                            textTransform: 'none',
                            '&:hover': { borderColor: '#93c5fd', bgcolor: '#f8fbff' },
                          }}
                        >
                          {React.isValidElement(action.icon)
                            ? React.cloneElement(action.icon, {
                                sx: {
                                  ...(action.icon.props?.sx || {}),
                                  fontSize: isCompactDesktop && !isMobile ? 18 : 20,
                                },
                              })
                            : action.icon}
                          <Typography sx={{ fontSize: isMobile ? '0.64rem' : (isCompactDesktop ? '0.66rem' : '0.74rem'), lineHeight: 1.15, color: '#475569', fontWeight: 700, textAlign: 'center' }}>
                            {action.label}
                          </Typography>
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {activeTab === 'notifications' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25, display: 'grid', gap: 0.8 }}>
                    {deviceNotifications.length ? deviceNotifications.map((alarm) => {
                      const alarmKey = alarm?._id || alarm?.alarmId || alarm?.id;
                      const isSelected = String(selectedNotificationId || "") === String(alarmKey || "");
                      const hasCoords = Boolean(getNotificationCoords(alarm));
                      return (
                        <Box
                          key={alarmKey}
                          onClick={() => hasCoords && onSelectNotification?.(alarm)}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '34px minmax(0, 1fr) auto',
                            gap: 1,
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 2.4,
                            border: isSelected ? '1px solid rgba(37,99,235,0.45)' : '1px solid rgba(226,232,240,0.95)',
                            bgcolor: isSelected ? '#eef5ff' : '#fff',
                            cursor: hasCoords ? 'pointer' : 'default',
                            '&:hover': { borderColor: hasCoords ? '#93c5fd' : 'rgba(226,232,240,0.95)', bgcolor: hasCoords ? '#f8fbff' : '#fff' },
                          }}
                        >
                          <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fff7ed', color: '#f97316' }}>
                            <NotificationsIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: compactText, fontWeight: 800, color: '#122033', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {prettyNotificationEvent(alarm?.alarmType || alarm?.eventType)}
                            </Typography>
                            <Typography sx={{ fontSize: compactLabel, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {formatNotificationTime(alarm?.triggeredAt || alarm?.timestamp)}
                            </Typography>
                          </Box>
                          <Chip size="small" label={hasCoords ? "Map" : "No GPS"} sx={{ height: 22, fontSize: '0.64rem', fontWeight: 800 }} />
                        </Box>
                      );
                    }) : (
                      <Box sx={{ p: 2, color: '#64748b', fontSize: compactText, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid rgba(226,232,240,0.95)' }}>
                        No notifications found.
                      </Box>
                    )}
                  </Box>
                ) : null}

                {activeTab === 'history' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25, display: 'grid', gap: 1 }}>
                    <Box sx={{ ...sectionCardSx, p: 1.2, boxShadow: 'none' }}>
                      <Typography sx={{ fontSize: compactTitle, fontWeight: 900, color: '#122033' }}>Vehicle History</Typography>
                      <Typography sx={{ mt: 0.4, fontSize: compactText, color: '#64748b' }}>
                        Open this vehicle route/history on map with the same history flow.
                      </Typography>
                      <Button
                        fullWidth
                        onClick={() => onShowHistory?.(device)}
                        variant="contained"
                        sx={{ mt: 1.1, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                      >
                        Open History
                      </Button>
                    </Box>
                  </Box>
                ) : null}

                {activeTab === 'geofence' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25, display: 'grid', gap: 0.85 }}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {onCreateSidebarGeofence ? (
                        <>
                          <Button size="small" variant="outlined" onClick={() => onCreateSidebarGeofence("polygon")} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}>Add Polygon</Button>
                          <Button size="small" variant="outlined" onClick={() => onCreateSidebarGeofence("circle")} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}>Add Circle</Button>
                        </>
                      ) : null}
                      {onCreateGeofence ? (
                        <Button size="small" variant="outlined" onClick={() => onCreateGeofence(device)} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}>Device Fence</Button>
                      ) : null}
                    </Box>
                    {geofenceRows.length ? geofenceRows.map((item) => {
                      const isSelected = String(selectedGeofenceId || "") === item.id;
                      return (
                        <Box
                          key={item.id}
                          onClick={() => onSelectGeofence?.(item.raw || item)}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '34px minmax(0, 1fr) auto',
                            gap: 1,
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 2.4,
                            border: isSelected ? '1px solid rgba(37,99,235,0.45)' : '1px solid rgba(226,232,240,0.95)',
                            bgcolor: isSelected ? '#eef5ff' : '#fff',
                            cursor: 'pointer',
                            '&:hover': { borderColor: '#93c5fd', bgcolor: '#f8fbff' },
                          }}
                        >
                          <Box sx={{ width: 30, height: 30, borderRadius: item.type === 'circle' ? '50%' : 2, display: 'grid', placeItems: 'center', bgcolor: '#eff6ff', color: '#2563eb' }}>
                            <GeofenceIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: compactText, fontWeight: 900, color: '#122033', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.name}
                            </Typography>
                            <Typography sx={{ fontSize: compactLabel, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'capitalize' }}>
                              {item.locationLabel || item.category || item.type} • {item.radiusText ? `${item.radiusText} radius` : `${item.assignedCount || 0} vehicle`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.2 }}>
                            {onEditSidebarGeofence ? <IconButton size="small" onClick={(event) => { event.stopPropagation(); onEditSidebarGeofence(item.raw || item); }}><EditIcon sx={{ fontSize: 17 }} /></IconButton> : null}
                            {onDeleteSidebarGeofence ? <IconButton size="small" onClick={(event) => { event.stopPropagation(); onDeleteSidebarGeofence(item.raw || item); }}><DeleteIcon sx={{ fontSize: 17 }} /></IconButton> : null}
                            {onAssignSidebarGeofence ? <IconButton size="small" onClick={(event) => { event.stopPropagation(); onAssignSidebarGeofence(item.raw || item); }}><AssignIcon sx={{ fontSize: 17 }} /></IconButton> : null}
                          </Box>
                        </Box>
                      );
                    }) : (
                      <Box sx={{ p: 2, color: '#64748b', fontSize: compactText, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid rgba(226,232,240,0.95)' }}>
                        No geofence found.
                      </Box>
                    )}
                  </Box>
                ) : null}

                {activeTab === 'landmark' ? (
                  <Box sx={{ p: isCompactDesktop && !isMobile ? 1 : 1.25, display: 'grid', gap: 0.85 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: compactTitle, fontWeight: 900, color: '#122033' }}>
                        Landmarks
                      </Typography>
                      {onCreateSidebarLandmark ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PlusIcon sx={{ fontSize: 16 }} />}
                          onClick={openCreateLandmarkForm}
                          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
                        >
                          Add
                        </Button>
                      ) : null}
                    </Box>

                    {landmarkFormOpen ? (
                      <Box sx={{ p: 1, borderRadius: 2.5, border: '1px solid rgba(226,232,240,0.95)', bgcolor: '#fff', display: 'grid', gap: 0.85 }}>
                        <Typography sx={{ fontSize: compactText, fontWeight: 900, color: '#122033' }}>
                          {landmarkMode === "edit" ? "Edit Landmark" : "Add Landmark"}
                        </Typography>
                        <TextField
                          size="small"
                          label="Name"
                          value={landmarkForm.name}
                          onChange={(event) => setLandmarkForm((prev) => ({ ...prev, name: event.target.value }))}
                        />
                        <TextField
                          size="small"
                          label="Address"
                          value={landmarkForm.address || landmarkLocationQuery}
                          onChange={(event) => {
                            setLandmarkLocationQuery(event.target.value);
                            setLandmarkForm((prev) => ({ ...prev, address: event.target.value }));
                          }}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                          <TextField
                            size="small"
                            label="Category"
                            value={landmarkForm.category}
                            onChange={(event) => setLandmarkForm((prev) => ({ ...prev, category: event.target.value }))}
                          />
                          <TextField
                            size="small"
                            label="Radius (m)"
                            type="number"
                            value={landmarkForm.radius}
                            onChange={(event) => {
                              const radius = Math.max(0, Number(event.target.value || 0));
                              setLandmarkForm((prev) => ({ ...prev, radius }));
                              onLandmarkDraftChange?.((currentDraft) => currentDraft ? { ...currentDraft, radius } : currentDraft);
                            }}
                          />
                        </Box>
                        <TextField
                          size="small"
                          label="Description"
                          value={landmarkForm.description}
                          onChange={(event) => setLandmarkForm((prev) => ({ ...prev, description: event.target.value }))}
                          multiline
                          minRows={2}
                        />
                        <Typography sx={{ fontSize: compactLabel, color: '#64748b', fontWeight: 700 }}>
                          Location: {Number.isFinite(Number(landmarkForm.latitude)) && Number.isFinite(Number(landmarkForm.longitude))
                            ? `${Number(landmarkForm.latitude).toFixed(6)}, ${Number(landmarkForm.longitude).toFixed(6)}`
                            : 'Draw circle on map or use current vehicle location'}
                        </Typography>
                        {landmarkFormError ? (
                          <Typography sx={{ fontSize: compactLabel, color: '#dc2626', fontWeight: 800 }}>
                            {landmarkFormError}
                          </Typography>
                        ) : null}
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={landmarkLocationLoading}
                            onClick={handleUseCurrentLandmarkLocation}
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
                          >
                            {landmarkLocationLoading ? "Detecting..." : "Use Vehicle Location"}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setLandmarkPlacementActive(true);
                              onLandmarkDraftChange?.(null);
                            }}
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
                          >
                            Draw Circle
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={landmarkSaving || landmarkLocationLoading}
                            onClick={handleLandmarkSubmit}
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 900 }}
                          >
                            {landmarkSaving ? "Saving..." : "Submit"}
                          </Button>
                          <Button size="small" onClick={closeLandmarkForm} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}>
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : null}

                    {landmarkRows.length ? landmarkRows.map((item) => {
                      const isSelected = String(selectedLandmarkId || "") === item.id;
                      return (
                      <Box
                        key={item.id}
                        onClick={() => onSelectLandmark?.(item.raw || item)}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '34px minmax(0, 1fr) auto',
                          gap: 1,
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 2.4,
                          border: isSelected ? '1px solid rgba(234,88,12,0.45)' : '1px solid rgba(226,232,240,0.95)',
                          bgcolor: isSelected ? '#fff7ed' : '#fff',
                          cursor: 'pointer',
                          '&:hover': { borderColor: '#fdba74', bgcolor: '#fff7ed' },
                        }}
                      >
                        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fff7ed', color: '#ea580c' }}>
                          <PlaceIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: compactText, fontWeight: 900, color: '#122033', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</Typography>
                          <Typography sx={{ fontSize: compactLabel, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.address || item.category}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                          <Chip size="small" label={`${item.radiusText || item.radius}m`} sx={{ height: 22, fontSize: '0.64rem', fontWeight: 800 }} />
                          {onUpdateSidebarLandmark ? <IconButton size="small" onClick={(event) => { event.stopPropagation(); openEditLandmarkForm(item.raw || item); }}><EditIcon sx={{ fontSize: 17 }} /></IconButton> : null}
                          {onDeleteSidebarLandmark ? <IconButton size="small" onClick={(event) => { event.stopPropagation(); onDeleteSidebarLandmark(item.raw || item); }}><DeleteIcon sx={{ fontSize: 17 }} /></IconButton> : null}
                          {onAssignSidebarLandmark ? <IconButton size="small" onClick={(event) => { event.stopPropagation(); onAssignSidebarLandmark(item.raw || item); }}><AssignIcon sx={{ fontSize: 17 }} /></IconButton> : null}
                        </Box>
                      </Box>
                    );}) : !landmarkFormOpen ? (
                      <Box sx={{ p: 2, color: '#64748b', fontSize: compactText, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid rgba(226,232,240,0.95)' }}>
                        No landmark found.
                      </Box>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Box>
        </Box>
        ) : null}
      </Paper>

      <BrandSelectionDialog
        open={assignDriverOpen}
        onClose={() => {
          setAssignDriverOpen(false);
          setDriverSearch("");
        }}
        title="Assign Driver"
        subtitle="Select a driver to assign to this vehicle."
        tone="green"
        search={driverSearch}
        onSearchChange={setDriverSearch}
        searchPlaceholder="Search driver by name or mobile number..."
        items={availableDrivers}
        selectedId={assignedDriver.id}
        onSelect={(driver) => !isAssigningDriver && handleAssignDriver(driver)}
        getPrimaryText={(driver) => driver.label}
        getSecondaryText={(driver) => driver.mobile || "NA"}
        getBadgeText={() => "Active"}
        countLabel={`Total ${availableDrivers.length} drivers`}
        actionLabel="Done"
        actionTone="green"
        onAction={() => {
          setAssignDriverOpen(false);
          setDriverSearch("");
        }}
        loading={isAssigningDriver}
      />
    </Box>
  );
};

export default RightSidebarPanel;
